import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/lib/utils';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { formatDistanceToNow } from 'date-fns';
import { enUS, he } from 'date-fns/locale';

interface MeetingCardProps {
  employeeName: string;
  employeeImageUrl?: string;
  lastMeetingDate?: string | null;
  isSelected: boolean;
  onSelect: () => void;
}

const getLastMeetingText = (
  lastMeetingDate: string | null | undefined,
  neverText: string,
  locale: 'en' | 'he'
) => {
  if (!lastMeetingDate) return neverText;
  const locales = {
    en: enUS,
    he: he,
  };
  return formatDistanceToNow(new Date(lastMeetingDate), {
    addSuffix: true,
    locale: locales[locale] || enUS,
  });
};

export const MeetingCard = ({
  employeeName,
  employeeImageUrl,
  lastMeetingDate,
  isSelected,
  onSelect,
}: MeetingCardProps) => {
  const { t, i18n } = useTranslation('oneOnOne');

  const lastMeetingText = getLastMeetingText(
    lastMeetingDate,
    t('never'),
    i18n.language as 'en' | 'he'
  );

  return (
    <Button
      variant="ghost"
      onClick={onSelect}
      className={cn(
        'h-auto w-full justify-start rounded-lg border p-3 transition-all',
        isSelected
          ? 'border-primary bg-primary/5 shadow-sm hover:bg-primary/10'
          : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
      )}
    >
      <div className="flex w-full items-center gap-3">
        <UserSelectorAvatar
          fullName={employeeName}
          imageUrl={employeeImageUrl}
          size="md"
          shape="square"
        />
        <div className="flex-1 min-w-0 text-start">
          <p className="text-sm font-medium text-slate-900 truncate">
            {employeeName}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('lastMeeting')}: {lastMeetingText}
          </p>
        </div>
      </div>
    </Button>
  );
};
